import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map, Observable, tap } from 'rxjs';
import { SubscriptionPlan, SubscriptionInterval } from '@gqlSchema';
import {
  UsageSummary,
  BillingHistoryResult,
  InstanceConfig,
} from '../models/subscription.models';
import {
  GET_INSTANCE_CONFIG,
  GET_MY_SUBSCRIPTION,
  GET_SUBSCRIPTION_PLANS,
  GET_BILLING_HISTORY,
} from '../graphql/subscription.queries';
import {
  CREATE_SUBSCRIPTION_CHECKOUT,
  CREATE_CUSTOMER_PORTAL_SESSION,
  CANCEL_SUBSCRIPTION,
  REACTIVATE_SUBSCRIPTION,
  CHANGE_PLAN,
  PURCHASE_BOOKING_TOP_UP,
} from '../graphql/subscription.mutations';

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  constructor(private apollo: Apollo) {}

  getInstanceConfig(): Observable<InstanceConfig> {
    return this.apollo
      .query<{ instanceConfig: InstanceConfig }>({
        query: GET_INSTANCE_CONFIG,
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data.instanceConfig));
  }

  getMySubscription(): Observable<UsageSummary> {
    return this.apollo
      .query<{ mySubscription: UsageSummary }>({
        query: GET_MY_SUBSCRIPTION,
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data.mySubscription));
  }

  getSubscriptionPlans(): Observable<SubscriptionPlan[]> {
    return this.apollo
      .query<{ subscriptionPlans: SubscriptionPlan[] }>({
        query: GET_SUBSCRIPTION_PLANS,
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data.subscriptionPlans));
  }

  getBillingHistory(limit?: number, startingAfter?: string): Observable<BillingHistoryResult> {
    return this.apollo
      .query<{ billingHistory: BillingHistoryResult }>({
        query: GET_BILLING_HISTORY,
        variables: { limit, startingAfter },
        fetchPolicy: 'network-only',
      })
      .pipe(map((result) => result.data.billingHistory));
  }

  createSubscriptionCheckout(planId: string, interval: SubscriptionInterval): Observable<string> {
    return this.apollo
      .mutate<{ createSubscriptionCheckout: string }>({
        mutation: CREATE_SUBSCRIPTION_CHECKOUT,
        variables: { planId, interval },
      })
      .pipe(
        map((result) => result.data!.createSubscriptionCheckout),
        tap((url) => {
          window.location.href = url;
        }),
      );
  }

  createCustomerPortalSession(): Observable<string> {
    return this.apollo
      .mutate<{ createCustomerPortalSession: string }>({
        mutation: CREATE_CUSTOMER_PORTAL_SESSION,
      })
      .pipe(
        map((result) => result.data!.createCustomerPortalSession),
        tap((url) => {
          window.location.href = url;
        }),
      );
  }

  cancelSubscription(): Observable<any> {
    return this.apollo
      .mutate<{ cancelSubscription: any }>({
        mutation: CANCEL_SUBSCRIPTION,
      })
      .pipe(map((result) => result.data!.cancelSubscription));
  }

  reactivateSubscription(): Observable<any> {
    return this.apollo
      .mutate<{ reactivateSubscription: any }>({
        mutation: REACTIVATE_SUBSCRIPTION,
      })
      .pipe(map((result) => result.data!.reactivateSubscription));
  }

  changePlan(planId: string, interval: SubscriptionInterval): Observable<any> {
    return this.apollo
      .mutate<{ changePlan: any }>({
        mutation: CHANGE_PLAN,
        variables: { planId, interval },
      })
      .pipe(map((result) => result.data!.changePlan));
  }

  purchaseBookingTopUp(extraBookings: number): Observable<any> {
    return this.apollo
      .mutate<{ purchaseBookingTopUp: any }>({
        mutation: PURCHASE_BOOKING_TOP_UP,
        variables: { extraBookings },
      })
      .pipe(map((result) => result.data!.purchaseBookingTopUp));
  }
}
